import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import {
  EngagementFeedWithUser,
  NewsfeedItemResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import {
  getListProductSourceDetails,
  isCommercialList,
} from '@epicuro-next/features/engagement/list-utils';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { List } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  AnalyticsSource,
  ProductListAnalyticsEvent,
} from '@epicuro-next/platform/event-tracker';
import {
  ProductSourceDetails,
  setProductSource,
} from '@epicuro-next/state/config-strip-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { go } from '@epicuro-next/state/router-store';
import {
  selectLookbookIdInRoute,
  selectLookbookListIdInRoute,
} from '@epicuro-next/state/shop-the-look-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'epc-lookbook-spotlight-item',
  templateUrl: './lookbook-spotlight-item.component.html',
  styleUrls: ['./lookbook-spotlight-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LookbookSpotlightItemComponent
  implements OnInit, OnChanges, OnDestroy
{
  public imageSizes = IMAGE_MAX_SIZES;
  public listIdClicked$$ = new Subject<string>();
  public collapseCommand$$ = new BehaviorSubject<boolean>(
    this.defaultCollapsed,
  );

  @Input()
  public set defaultCollapsed(value: boolean) {
    this.collapseCommand$$.next(value);
  }

  @Input()
  public article?: EngagementFeedWithUser;

  @Input()
  public referenceIds: string[];

  @Output()
  public referenceClicked = new EventEmitter<NewsfeedItemResponseDto>();

  @Output()
  public collapsed = new EventEmitter<boolean>();

  private lookBookId$$ = new BehaviorSubject<string | undefined>(undefined);

  public lookBookListIdInRouter$ = this.store.pipe(
    select(selectLookbookListIdInRoute),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public lookbookIdInRoute$ = this.store.pipe(
    select(selectLookbookIdInRoute),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public currentLookBookIsInRouter$ = combineLatest([
    this.lookBookId$$,
    this.lookbookIdInRoute$,
  ]).pipe(
    map(([lookBookId, lookBookIdInRouter]) => {
      if (!lookBookId || !lookBookIdInRouter) return undefined;
      return lookBookId === lookBookIdInRouter;
    }),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  public collapsed$ = merge(
    this.currentLookBookIsInRouter$.pipe(map((value) => !value)), // router can only expand, cannot collapse
    this.collapseCommand$$,
  ).pipe(tap((isCollapsed) => this.collapsed.emit(isCollapsed)));

  public productListAnalyticsEvent = ProductListAnalyticsEvent;
  public analyticsSource = AnalyticsSource;

  private destroy$$ = new Subject<void>();

  constructor(private store: Store) {}

  public get lookBookId() {
    return this.article?.referenceId ?? undefined;
  }

  public get listReferences() {
    return this.article?.references;
  }

  public isListCommercialList(type?: string | null) {
    if (isNullish(type)) return false;
    return isCommercialList({ type });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const articleChange = changes.article;

    if (articleChange) this.handleArticleChange(articleChange);
  }

  public ngOnInit() {
    this.handleClickNavigationEvents();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public stopEvent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  public setSource(list: List) {
    const listId = list.id;
    const source = BasketItemSource.LIST_SPOTLIGHT;
    const details: ProductSourceDetails = getListProductSourceDetails(list);

    if (this.lookBookId) {
      details.lookBookId = [this.lookBookId];
    }

    this.store.dispatch(
      setProductSource({
        source,
        details,
      }),
    );

    this.store.dispatch(
      trackList({
        payload: {
          source: 'spotlight',
          productListId: listId,
        },
      }),
    );
  }

  private handleClickNavigationEvents() {
    this.listIdClicked$$.pipe(takeUntil(this.destroy$$)).subscribe((listId) => {
      const path = [this.article?.referenceId, listId].filter(isNonNullish);

      this.store.dispatch(
        go({
          commands: [
            '/',
            APP_PRIVATE_PATHS.browse.path,
            APP_PRIVATE_PATHS.shopTheLook.path,
            ...path,
          ],
        }),
      );
    });
  }

  private handleArticleChange(change: SimpleChange) {
    const prev: EngagementFeedWithUser = change.previousValue;
    const curr: EngagementFeedWithUser = change.currentValue;

    if (change.firstChange || prev?.id !== curr?.id) {
      this.lookBookId$$.next(this.article?.referenceId ?? undefined);
    }
  }
}
